import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Upload, Loader2, Download, RefreshCw, ImageIcon, MoveHorizontal, ShoppingBag, Share2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { ShareLookMenu } from "../components/ShareLookMenu";
import { inr } from "../lib/format";
import { shopLook } from "../lib/lookActions";
import { toast } from "sonner";

export default function TryOn() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { addItem } = useCart();
  const fileRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(params.get("product") || "");
  const [photo, setPhoto] = useState(null); // data url
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [looks, setLooks] = useState([]);
  const [customDesign, setCustomDesign] = useState(null); // { image, name }

  const loadLooks = () => {
    if (user) api.get("/tryon/looks").then((r) => setLooks(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (!selected && r.data.length) setSelected(r.data[0].id);
    }).catch(() => {});
    // Custom AI design handed off from the Designer page
    if (params.get("design")) {
      try {
        const d = JSON.parse(sessionStorage.getItem("tashora_pending_design"));
        if (d?.image) setCustomDesign(d);
      } catch (e) { console.warn("Could not parse pending design from sessionStorage:", e); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadLooks(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Please use an image under 8MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setPhoto(reader.result); setResult(null); };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!photo) { toast.error("Upload a portrait first"); return; }
    if (!customDesign && !selected) { toast.error("Pick a saree to try on"); return; }
    setLoading(true);
    setResult(null);
    try {
      const payload = customDesign
        ? { image_base64: photo, design_image: customDesign.image, design_name: customDesign.name }
        : { product_id: selected, image_base64: photo };
      const res = await api.post("/tryon", payload);
      setResult(res.data.image);
      toast.success("Your drape is ready!");
      loadLooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed. Try another photo.");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = result;
    a.download = "tashora-tryon.png";
    a.click();
  };

  const selectedProduct = products.find((p) => p.id === selected);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="tryon-page">
      <div className="text-center mb-12">
        <span className="caption">AI Virtual Draping Studio</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-gold-light mt-3">
          See it <span className="gold-text italic">draped on you</span>
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
          Upload a clear portrait, pick a saree, and our AI will realistically drape it over you.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
        {/* Controls */}
        <div className="space-y-6">
          <div>
            <p className="caption mb-3">Step 1 · Your photo</p>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="photo-input" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="upload-photo-button"
              className="w-full aspect-[3/2] rounded-lg gold-border bg-obsidian-2 grid place-items-center hover:bg-gold/5 transition-colors overflow-hidden relative"
            >
              {photo ? (
                <img src={photo} alt="Your portrait" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gold mx-auto" />
                  <p className="text-sm text-gold-muted mt-3">Click to upload a portrait</p>
                  <p className="text-xs text-muted-foreground mt-1">Front-facing, well-lit works best</p>
                </div>
              )}
            </button>
            {photo && (
              <button onClick={() => fileRef.current?.click()} className="text-xs text-gold mt-2 inline-flex items-center gap-1" data-testid="change-photo-button">
                <RefreshCw className="w-3 h-3" /> Change photo
              </button>
            )}
          </div>

          <div>
            <p className="caption mb-3">Step 2 · {customDesign ? "Your AI design" : "Choose a saree"}</p>
            {customDesign ? (
              <div className="flex items-center gap-4 p-3 rounded-lg gold-border bg-obsidian-2" data-testid="custom-design-banner">
                <img src={customDesign.image} alt="Custom design" className="w-16 h-20 object-cover rounded-md" />
                <div className="flex-1">
                  <p className="text-gold-light font-serif text-lg">{customDesign.name}</p>
                  <p className="text-xs text-muted-foreground">Your AI-designed saree</p>
                  <button onClick={() => setCustomDesign(null)} data-testid="use-catalog-instead" className="text-xs text-gold mt-1 hover:underline">
                    Use a catalog saree instead
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[240px] overflow-y-auto pr-1">
                  {products.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelected(p.id)}
                      data-testid={`tryon-select-${p.id}`}
                      className={`relative rounded-md overflow-hidden aspect-[3/4] border-2 transition-colors ${selected === p.id ? "border-gold" : "border-transparent hover:border-gold/40"}`}
                    >
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                {selectedProduct && (
                  <p className="text-sm text-gold-muted mt-3">{selectedProduct.name} · <span className="font-mono text-gold">{inr(selectedProduct.price)}</span></p>
                )}
              </>
            )}
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-tryon-button"
            className="w-full bg-gold text-obsidian font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Draping… this takes ~15s" : "Generate my look"}
          </button>
          {!user && <p className="text-xs text-muted-foreground text-center">Tip: sign in to save your generated looks to your account.</p>}
        </div>

        {/* Result */}
        <div>
          <p className="caption mb-3 flex items-center gap-2">
            Result
            {result && photo && <span className="text-muted-foreground normal-case tracking-normal font-sans inline-flex items-center gap-1"><MoveHorizontal className="w-3 h-3 text-gold" /> drag to compare</span>}
          </p>
          {result && photo ? (
            <BeforeAfterSlider before={photo} after={result} />
          ) : (
            <div className="relative rounded-lg overflow-hidden gold-border bg-obsidian-2 aspect-[4/5]" data-testid="tryon-result">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 grid place-items-center">
                    <div className="text-center">
                      <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="caption mt-5">Weaving your drape</p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 grid place-items-center text-center px-8">
                    <div>
                      <ImageIcon className="w-10 h-10 text-gold/40 mx-auto" />
                      <p className="text-sm text-muted-foreground mt-4">Your AI-draped portrait will appear here.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          {result && (
            <div className={`mt-4 grid grid-cols-1 gap-3 ${customDesign ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
              <button onClick={download} data-testid="download-button" className="border border-gold/40 text-gold-muted py-3 rounded-full hover:bg-gold/10 transition-colors flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Download
              </button>
              <ShareLookMenu image={result} productName={customDesign?.name || selectedProduct?.name} align="center">
                <button data-testid="share-look-button" className="border border-gold/40 text-gold-muted py-3 rounded-full hover:bg-gold/10 transition-colors flex items-center justify-center gap-2">
                  <Share2 className="w-4 h-4 text-gold" /> Share my look
                </button>
              </ShareLookMenu>
              {!customDesign && (
                <button onClick={() => selectedProduct && shopLook(selectedProduct.id, addItem)} data-testid="shop-look-button" className="bg-gold text-obsidian font-semibold py-3 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
                  <ShoppingBag className="w-4 h-4" /> Shop this saree
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Saved looks */}
      {user && looks.length > 0 && (
        <div className="mt-20">
          <div className="hairline mb-8" />
          <h2 className="font-serif text-3xl text-gold-light mb-6">Your saved looks</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="saved-looks">
            {looks.map((l) => (
              <div key={l.look_id} className="rounded-lg overflow-hidden gold-border bg-obsidian-2" data-testid={`saved-look-${l.look_id}`}>
                <img src={l.image} alt={l.product_name} className="w-full aspect-[4/5] object-cover" />
                <p className="text-xs text-gold-muted px-2 pt-2 truncate">{l.product_name}</p>
                <div className="flex gap-1.5 p-2">
                  {l.product_id && (
                    <button onClick={() => shopLook(l.product_id, addItem)} data-testid={`saved-shop-${l.look_id}`} className="flex-1 text-[11px] bg-gold text-obsidian font-semibold py-1.5 rounded hover:bg-gold-light transition-colors flex items-center justify-center gap-1">
                      <ShoppingBag className="w-3 h-3" /> Shop
                    </button>
                  )}
                  <ShareLookMenu image={l.image} productName={l.product_name}>
                    <button data-testid={`saved-share-${l.look_id}`} aria-label="Share look" className={`${l.product_id ? "w-8" : "flex-1"} grid place-items-center border border-gold/40 text-gold rounded hover:bg-gold/10 transition-colors py-1.5`}>
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  </ShareLookMenu>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
